/**
 * Platform-Assisted Onboarding API — a platform operator writes a NAMED tenant's
 * onboarding configuration on the tenant's behalf.
 *
 * The tenant is selected by PATH PARAMETER; the authority is the platform grant
 * `tenant.update`.
 *
 * CONTRACT SOURCE: contracts/openapi/foundation.v1.yaml (register v1.4).
 *
 * ---------------------------------------------------------------------------
 * WHY THE LOCATION SHAPES LIVE HERE
 * ---------------------------------------------------------------------------
 * This surface writes the ONBOARDING location — a labelled, publishable record
 * with a type and a primary flag. It is NOT the canonical location: the
 * canonical location is a vendor-profile resource with a private address and a
 * coordinate, and it lives in `workspace-api.ts`. The two are different
 * contracts, so the onboarding shape is stated HERE rather than borrowed.
 *
 * The coverage (service-area) shapes ARE the same contract on both surfaces,
 * so those stay shared with `workspace-api.ts`.
 */
import { http, generateIdempotencyKey } from '@/api/client/http';
import { z } from 'zod';
import { UuidSchema } from '@/api/schemas/common';
import {
  BusinessProfileResponseSchema,
  ServiceAreaIdResponseSchema,
  ServiceAreaListResponseSchema,
  SingleVerticalResponseSchema,
  VerticalListResponseSchema,
} from '@/modules/tenants/services/workspace-api';
import type {
  BusinessProfile,
  BusinessProfilePatch,
  ServiceArea,
  AssignServiceAreaRequest,
  TenantVertical,
  AssignVerticalRequest,
  UpdateVerticalRequest,
} from '@/modules/tenants/services/workspace-api';

const meta = z.object({ requestId: z.string().max(128) });

// ============================================================================
// The onboarding location (this surface's own contract)
// ============================================================================

const OnboardingLocationTypeSchema = z.enum([
  'storefront',
  'branch',
  'kitchen',
  'operating_base',
  'pickup',
]);

export const PlatformLocationSchema = z.object({
  id: UuidSchema,
  label: z.string(),
  locationType: OnboardingLocationTypeSchema.nullable(),
  isPrimary: z.boolean(),
  isCustomerFacing: z.boolean(),
  publicationStatus: z.enum(['draft', 'published', 'archived']),
  country: z.string(),
  displayRegion: z.string().nullable(),
  addressLines: z.array(z.string()),
  version: z.number().int().min(1),
});

export type PlatformLocation = z.infer<typeof PlatformLocationSchema>;

export const PlatformCreateLocationRequestSchema = z.object({
  label: z.string().min(1).max(200),
  addressLines: z.array(z.string().max(200)).max(10).optional(),
  locationType: OnboardingLocationTypeSchema.optional(),
  isCustomerFacing: z.boolean().optional(),
});

export type PlatformCreateLocationRequest = z.infer<typeof PlatformCreateLocationRequestSchema>;

export const PlatformUpdateLocationRequestSchema = z.object({
  label: z.string().min(1).max(200).optional(),
  locationType: OnboardingLocationTypeSchema.optional(),
  isCustomerFacing: z.boolean().optional(),
  displayRegion: z.string().max(200).nullable().optional(),
  addressLines: z.array(z.string().max(200)).max(10).optional(),
});

export type PlatformUpdateLocationRequest = z.infer<typeof PlatformUpdateLocationRequestSchema>;

const PlatformLocationListResponseSchema = z.object({
  data: z.array(PlatformLocationSchema),
  meta,
});

const PlatformLocationResponseSchema = z.object({
  data: PlatformLocationSchema,
  meta,
});

/** The branding patch shape (banner + colour), mirroring `PatchBrandingRequestSchema`. */
export const PlatformBrandingPatchSchema = z
  .object({
    bannerDocumentId: UuidSchema.nullable().optional(),
    primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  })
  .strict();
export type PlatformBrandingPatch = z.infer<typeof PlatformBrandingPatchSchema>;

const BrandingResponseSchema = z.object({
  data: z.object({
    bannerDocumentId: UuidSchema.nullable(),
    primaryColor: z.string().nullable(),
  }),
  meta,
});

export const PlatformOnboardingApi = {
  // -------------------------------------------------------------------------
  // Profile + branding
  // -------------------------------------------------------------------------

  /** PATCH /tenants/{tenantId}/profile — write the named tenant's business profile. */
  async patchProfile(tenantId: string, patch: BusinessProfilePatch): Promise<BusinessProfile> {
    const response = await http.patch(
      `/tenants/${tenantId}/profile`,
      BusinessProfileResponseSchema,
      { body: patch, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** PATCH /tenants/{tenantId}/branding — write the named tenant's branding. */
  async patchBranding(
    tenantId: string,
    patch: PlatformBrandingPatch,
  ): Promise<{ bannerDocumentId: string | null; primaryColor: string | null }> {
    const response = await http.patch(`/tenants/${tenantId}/branding`, BrandingResponseSchema, {
      body: patch,
      idempotencyKey: generateIdempotencyKey(),
    });
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Verticals
  // -------------------------------------------------------------------------

  /** GET /tenants/{tenantId}/verticals — the named tenant's assigned verticals. */
  async listVerticals(tenantId: string): Promise<TenantVertical[]> {
    const response = await http.get(`/tenants/${tenantId}/verticals`, VerticalListResponseSchema);
    return response.data;
  },

  /** POST /tenants/{tenantId}/verticals — assign a vertical to the named tenant. */
  async assignVertical(tenantId: string, input: AssignVerticalRequest): Promise<TenantVertical> {
    const response = await http.post(
      `/tenants/${tenantId}/verticals`,
      SingleVerticalResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** PATCH /tenants/{tenantId}/verticals/{verticalCode} — update a vertical assignment. */
  async updateVertical(
    tenantId: string,
    verticalCode: string,
    input: UpdateVerticalRequest,
  ): Promise<TenantVertical> {
    const response = await http.patch(
      `/tenants/${tenantId}/verticals/${verticalCode}`,
      SingleVerticalResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Locations
  // -------------------------------------------------------------------------

  /** GET /tenants/{tenantId}/locations — the named tenant's locations. */
  async listLocations(tenantId: string): Promise<PlatformLocation[]> {
    const response = await http.get(
      `/tenants/${tenantId}/locations`,
      PlatformLocationListResponseSchema,
    );
    return response.data;
  },

  /** POST /tenants/{tenantId}/locations — create a location for the named tenant. */
  async createLocation(
    tenantId: string,
    input: PlatformCreateLocationRequest,
  ): Promise<PlatformLocation> {
    const response = await http.post(
      `/tenants/${tenantId}/locations`,
      PlatformLocationResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** PATCH /tenants/{tenantId}/locations/{locationId} — update a location. */
  async updateLocation(
    tenantId: string,
    locationId: string,
    input: PlatformUpdateLocationRequest,
  ): Promise<PlatformLocation> {
    const response = await http.patch(
      `/tenants/${tenantId}/locations/${locationId}`,
      PlatformLocationResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** POST /tenants/{tenantId}/locations/{locationId}/primary — mark a location primary. */
  async setPrimaryLocation(
    tenantId: string,
    locationId: string,
  ): Promise<{ locationId: string; isPrimary: true }> {
    const response = await http.post(
      `/tenants/${tenantId}/locations/${locationId}/primary`,
      z.object({
        data: z.object({ locationId: UuidSchema, isPrimary: z.literal(true) }),
        meta,
      }),
      { idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** PATCH /tenants/{tenantId}/locations/{locationId}/status — publish/archive a location. */
  async setLocationStatus(
    tenantId: string,
    locationId: string,
    status: 'draft' | 'published' | 'archived',
  ): Promise<{ locationId: string; status: string }> {
    const response = await http.patch(
      `/tenants/${tenantId}/locations/${locationId}/status`,
      z.object({
        data: z.object({ locationId: UuidSchema, status: z.string() }),
        meta,
      }),
      { body: { status }, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Service areas
  // -------------------------------------------------------------------------

  /** GET /tenants/{tenantId}/service-areas — the named tenant's service areas. */
  async listServiceAreas(tenantId: string): Promise<ServiceArea[]> {
    const response = await http.get(
      `/tenants/${tenantId}/service-areas`,
      ServiceAreaListResponseSchema,
    );
    return response.data;
  },

  /** POST /tenants/{tenantId}/service-areas — assign a service area. */
  async assignServiceArea(
    tenantId: string,
    input: AssignServiceAreaRequest,
  ): Promise<{ id: string }> {
    const response = await http.post(
      `/tenants/${tenantId}/service-areas`,
      ServiceAreaIdResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** PATCH /tenants/{tenantId}/service-areas/{serviceAreaId} — update a service area. */
  async updateServiceArea(
    tenantId: string,
    serviceAreaId: string,
    input: { modes?: string[]; radiusMeters?: number; status?: 'active' | 'disabled' },
  ): Promise<{ id: string }> {
    const response = await http.patch(
      `/tenants/${tenantId}/service-areas/${serviceAreaId}`,
      ServiceAreaIdResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },
};

export type {
  BusinessProfile,
  BusinessProfilePatch,
  ServiceArea,
  AssignServiceAreaRequest,
  TenantVertical,
  AssignVerticalRequest,
  UpdateVerticalRequest,
};
